import { Injectable, OnDestroy } from '@angular/core';
import { SocketService } from '@common/services/socket.service';
import { UserService } from '@modules/auth/services';
import { BehaviorSubject, EMPTY, fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs';
import { auditTime, catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { casheaMethodsOf, CasheaService } from './cashea.service';

/** Lo emite la API a la sala del comercio cuando cambia el estado de una orden de Link. */
export const CASHEA_LINK_CHANGED_EVENT = 'cashea_link_changed';

/** Respaldo por si se pierde un aviso del socket. El job que consulta a Cashea también corre cada 5 minutos. */
const FALLBACK_REFRESH_MS = 5 * 60 * 1000;

/**
 * Cuántas órdenes de Cashea Link están listas para finalizar (el cliente ya
 * pagó y falta cerrar la venta), para el contador del menú.
 *
 * Se vuelve a pedir cuando la API avisa por socket que cambió una orden, al
 * reconectar, al volver a la pestaña y cada 5 minutos. Solo para comercios con
 * Link habilitado; sin Link queda en 0 y no consulta nada.
 */
@Injectable({ providedIn: 'root' })
export class CasheaLinkBadgeService implements OnDestroy {
    private readonly _readyCount$ = new BehaviorSubject<number>(0);
    private readonly refresh$ = new Subject<void>();
    private readonly subscription: Subscription;

    get readyCount$(): Observable<number> {
        return this._readyCount$.asObservable();
    }

    constructor(userService: UserService, private casheaService: CasheaService, private socketService: SocketService) {
        this.subscription = userService.tenant$
            .pipe(
                map((tenant: any) => (tenant?.cashea?.enabled && casheaMethodsOf(tenant).link ? String(tenant.tenantId) : null)),
                distinctUntilChanged(),
                switchMap(tenantId => {
                    if (!tenantId) {
                        this._readyCount$.next(0);
                        return EMPTY;
                    }
                    return merge(
                        timer(0, FALLBACK_REFRESH_MS),
                        this.socketService.on(CASHEA_LINK_CHANGED_EVENT),
                        this.socketService.reconnect$,
                        fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
                        this.refresh$,
                    ).pipe(
                        // Varios avisos seguidos (una tanda del job) terminan en una sola consulta.
                        auditTime(500),
                        switchMap(() => this.casheaService.getLinkOrdersSummary$().pipe(catchError(() => EMPTY))),
                    );
                }),
            )
            .subscribe(({ ready }) => this._readyCount$.next(ready));
    }

    /** Para pedir el número en el momento, por ejemplo después de finalizar una orden. */
    refresh(): void {
        this.refresh$.next();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
